/**
 * Copyright(c) 2022 Fenix Outdoor International AG.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Fenix ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Fenix.
 *
 */
import React from "react";
import PropTypes from "prop-types";
import Collapse from "../../../common/core/Collapse";
import Button from "../../../common/core/Button";
import Col from "../../../common/core/Col";
import Spinner from "../../../common/core/Spinner";
import StatusIcon from "./StatusIcon";
import Permission from "../../../common/security/Permission";
import Rules from "../../../common/security/PermissionRules";

const CustomerData = (props) => {
  const {
    customers,
    collapsesOpen,
    toggleCollapses,
    toggleEditPage,
    isLoading,
  } = props;

  return (
    <ul className="data-list">
      { (customers && customers.customerAccounts) && !isLoading ? (
        <>
          {
            customers && customers.customerAccounts
            && customers.customerAccounts.map((customer) => {
              const isOpen = collapsesOpen.includes(customer.accountNo);
              return (
                <li className={`data-list-item ${isOpen ? "active" : ""}`}>
                  <div className="data-list-col">
                    <a href className="data-list-head" onClick={() => toggleCollapses(customer.accountNo)}>
                      <div className="data-list-status">
                        <StatusIcon
                          customer={customer}
                        />
                      </div>
                      <Col md="auto" className="data-arrow">
                        <Button color="arrow" onClick={() => toggleCollapses(customer)}>
                          <i className="fal fa-angle-right"> </i>
                        </Button>
                      </Col>
                      <Col xs="auto" className="data-ca">
                        {customer && customer.accountNo
                          ? (<strong>{customer.accountNo}</strong>) : <>&mdash;</>}
                      </Col>
                      <Col xs="auto" className="data-ia">
                        {customer && customer.invoiceAccountNo
                          ? (<strong>{customer.invoiceAccountNo}</strong>) : <>&mdash;</>}
                      </Col>
                      <Col xs="auto" className="data-name">
                        {customer && customer.accountName
                          ? (<strong>{customer.accountName}</strong>) : <>&mdash;</>}
                      </Col>
                    </a>
                    <Permission
                      allowed={Rules.Control.EDIT_CUSTOMER}
                    >
                      <Button color="edit" onClick={() => toggleEditPage(customer)}>
                        <i className="fal fa-pen"> </i>
                      </Button>
                    </Permission>
                  </div>
                  <Collapse isOpen={collapsesOpen.includes(customer.accountNo)} className="data-list-content">
                    <div className="data-content">
                      <div className="data-content-item">
                        <div className="data-content-head">Classification</div>
                        {customer && customer.classification
                          ? (<div className="data-content-text">{customer.classification}</div>) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">MozCom User</div>
                        {customer && customer.mozcomUsers ? (
                          <>
                            {
                              customer.mozcomUsers.map((mozcomUser) => (
                                <>
                                  {mozcomUser && mozcomUser.name ? (
                                    <div className="data-content-text">{mozcomUser.name}</div>
                                  ) : <>&mdash;</>}
                                </>
                              ))
                            }
                          </>
                        ) : (
                          <>&mdash;</>
                        )}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">MozCom User Email</div>
                        {customer && customer.mozcomUsers ? (
                          <>
                            {
                              customer.mozcomUsers.map((mozcomUser) => (
                                <>
                                  {mozcomUser && mozcomUser.email ? (
                                    <div className="data-content-text">{mozcomUser.email}</div>
                                  ) : <>&mdash;</>}
                                </>
                              ))
                            }
                          </>
                        ) : (
                          <>&mdash;</>
                        )}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">SR Name</div>
                        {customer && customer.salesRep && customer.salesRep.name
                          ? (<div className="data-content-text">{customer.salesRep.name}</div>) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">SR Email</div>
                        {customer && customer.salesRep && customer.salesRep.email
                          ? (<div className="data-content-text">{customer.salesRep.email}</div>) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">SR Phone</div>
                        {customer && customer.salesRep
                          && customer.salesRep.phone
                          && customer.salesRep.phone.dialCode && customer.salesRep.phone.phoneNumber
                          ? (
                            <div className="data-content-text">
                              {`${customer.salesRep.phone.dialCode} - ${customer.salesRep.phone.phoneNumber}`}
                            </div>
                          ) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">CSC Name</div>
                        {customer && customer.csr && customer.csr.name
                          ? (<div className="data-content-text">{customer.csr.name}</div>) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">CSC Email</div>
                        {customer && customer.csr && customer.csr.email
                          ? (<div className="data-content-text">{customer.csr.email}</div>) : <>&mdash;</>}
                      </div>
                      <div className="data-content-item">
                        <div className="data-content-head">CSC Phone</div>
                        {customer && customer.csr
                          && customer.csr.phone
                          && customer.csr.phone.dialCode && customer.csr.phone.phoneNumber
                          ? (
                            <div className="data-content-text">
                              {`${customer.csr.phone.dialCode} - ${customer.csr.phone.phoneNumber}`}
                            </div>
                          ) : <>&mdash;</>}
                      </div>
                    </div>
                  </Collapse>
                </li>
              );
            })
          }
        </>
      ) : (
        <div className="spinner-load d-flex align-items-center justify-content-center">
          <Spinner />
        </div>
      )}
    </ul>
  );
};

CustomerData.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.any).isRequired,
  collapsesOpen: PropTypes.arrayOf(PropTypes.any).isRequired,
  toggleCollapses: PropTypes.func.isRequired,
  toggleEditPage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default CustomerData;
